class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");
    if (!this.mainDetailsToggle) return;

    this.summary = this.mainDetailsToggle.querySelector("summary");
    this.content = this.summary?.nextElementSibling;

    this.syncAriaExpanded();

    this.mainDetailsToggle.addEventListener("focusout", this.onFocusOut.bind(this));
    this.summary?.addEventListener("click", (e) => this.onSummaryClick(e));
  }

  syncAriaExpanded(forceValue) {
    if (!this.summary) return;
    const isOpen =
      typeof forceValue === "boolean"
        ? forceValue
        : this.mainDetailsToggle.hasAttribute("open");
    this.summary.setAttribute("aria-expanded", isOpen ? "true" : "false");
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close(true);
    });
  }

  onSummaryClick(e) {
    e.preventDefault();

    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.close(true);
    } else {
      this.open(true);
    }
  }

  parseTimeListToMs(list) {
    return (list || "")
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean)
      .map((t) => {
        if (t.endsWith("ms")) return parseFloat(t);
        if (t.endsWith("s")) return parseFloat(t) * 1000;
        const n = parseFloat(t);
        return Number.isFinite(n) ? n : 0;
      });
  }

  getMaxTimingMs(el) {
    if (!el) return 0;
    const cs = window.getComputedStyle(el);

    const tDur = this.parseTimeListToMs(cs.transitionDuration);
    const tDel = this.parseTimeListToMs(cs.transitionDelay);
    const tCount = Math.max(tDur.length, tDel.length, 1);
    const tMax = Array.from({ length: tCount }, (_, i) => {
      const d = tDur[i % tDur.length] ?? 0;
      const l = tDel[i % tDel.length] ?? 0;
      return d + l;
    }).reduce((a, b) => Math.max(a, b), 0);

    const aDur = this.parseTimeListToMs(cs.animationDuration);
    const aDel = this.parseTimeListToMs(cs.animationDelay);
    const aIter = (cs.animationIterationCount || "")
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean)
      .map((v) => (v === "infinite" ? 1 : parseFloat(v)))
      .map((n) => (Number.isFinite(n) && n > 0 ? n : 1));

    const aCount = Math.max(aDur.length, aDel.length, aIter.length, 1);
    const aMax = Array.from({ length: aCount }, (_, i) => {
      const d = aDur[i % aDur.length] ?? 0;
      const l = aDel[i % aDel.length] ?? 0;
      const it = aIter[i % aIter.length] ?? 1;
      return d * it + l;
    }).reduce((a, b) => Math.max(a, b), 0);

    return Math.max(tMax, aMax);
  }

  closeOtherMegaMenusInstantly() {
    if (!this.mainDetailsToggle.classList.contains("mega-menu")) return;

    document.querySelectorAll("details.mega-menu[open]").forEach((d) => {
      if (d === this.mainDetailsToggle) return;

      d.classList.remove("is-closing", "is-opening");
      d.removeAttribute("open");

      const s = d.querySelector("summary");
      if (s) s.setAttribute("aria-expanded", "false");
    });
  }

  open(animated = true) {
    this.closeOtherMegaMenusInstantly();
    if (this.mainDetailsToggle.hasAttribute("open")) return;

    this.mainDetailsToggle.classList.remove("is-closing");

    if (!animated) {
      this.mainDetailsToggle.classList.remove("is-opening");
      this.mainDetailsToggle.setAttribute("open", "");
      this.syncAriaExpanded(true);
      return;
    }

    this.mainDetailsToggle.classList.add("is-opening");
    this.mainDetailsToggle.setAttribute("open", "");
    this.syncAriaExpanded(true);

    if (this.content) void this.content.offsetHeight;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.mainDetailsToggle.classList.remove("is-opening");
      });
    });
  }

  close(animated = true) {
    if (!this.mainDetailsToggle.hasAttribute("open")) return;

    this.syncAriaExpanded(false);

    if (!animated) {
      this.mainDetailsToggle.classList.remove("is-opening", "is-closing");
      this.mainDetailsToggle.removeAttribute("open");
      return;
    }

    this.mainDetailsToggle.classList.remove("is-opening");
    this.mainDetailsToggle.classList.add("is-closing");

    const ms = this.getMaxTimingMs(this.content);
    const CLOSE_MS = Math.max(50, Math.ceil(ms));

    window.setTimeout(() => {
      this.mainDetailsToggle.classList.remove("is-closing");
      this.mainDetailsToggle.removeAttribute("open");
    }, CLOSE_MS);
  }
}

customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector(".header-wrapper");
  }

  open(animated = true) {
    super.open(animated);

    if (this.header) {
      this.header.preventHide = true;

      if (document.documentElement.style.getPropertyValue("--header-bottom-position-desktop") === "") {
        document.documentElement.style.setProperty(
          "--header-bottom-position-desktop",
          `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
        );
      }
    }
  }

  close(animated = true) {
    super.close(animated);

    if (this.header) {
      this.header.preventHide = false;
    }
  }
}

customElements.define("header-menu", HeaderMenu);